import React from "react";
import { Typography } from "antd";

const { Title, Paragraph, Text, Link } = Typography;

export const PrivacyPolicy = () => {
  return (
    <div style={{ maxWidth: 800, margin: "0 auto", padding: "40px 20px" }}>
      <Title level={2}>ClubPass Privacy Policy</Title>

      <Text type="secondary">Effective Date: November 1, 2025</Text>
      <br />
      <Text type="secondary">Last Updated: October 13, 2025</Text>

      <Paragraph style={{ marginTop: 24 }}>
        This Privacy Policy explains how Train Your Pulse Ltd. (“we”, “us”, “our”) collects, uses,
        and protects your personal information when you use the ClubPass mobile application and
        website (together, the “Platform”).
      </Paragraph>

      <Paragraph>
        We are committed to protecting your privacy and ensuring transparency about how your data is
        handled in compliance with the UK GDPR, the EU General Data Protection Regulation (GDPR),
        and applicable Greek data protection laws.
      </Paragraph>

      <Title level={3}>1. Who We Are</Title>
      <Paragraph>
        Train Your Pulse Ltd.
        <br />
        Registered in the United Kingdom
        <br />
        Company number: +302114445860
        <br />
        Registered address: 1 Ffordd-Yr-Ywen, Pontypridd, United Kingdom, CF38 1TE
        <br />
        Email: <Link href="mailto:kg@clubpass.global">kg@clubpass.global</Link>
        <br />
        Train Your Pulse Ltd. is the data controller responsible for your personal data when you use
        ClubPass.
      </Paragraph>

      <Title level={3}>2. What Data We Collect</Title>
      <Paragraph>
        We collect the following types of personal data depending on how you interact with our
        Platform:
      </Paragraph>
      <Paragraph>
        <Text strong>a. Information you provide directly:</Text>
        <br />
        Name, surname, and contact details (email, phone number)
        <br />
        Account login details (username and password)
        <br />
        Payment preferences (processed securely via PAYMIX)
        <br />
        Profile information (interests, preferred activities)
        <br />
        Communication content (messages, feedback, support requests)
      </Paragraph>

      <Paragraph>
        <Text strong>b. Information we collect automatically:</Text>
        <br />
        Device and browser information
        <br />
        IP address and location (approximate)
        <br />
        Usage data such as pages visited, searches, bookings, and time spent
        <br />
        Cookies and tracking technologies (see Cookie Notice below)
      </Paragraph>

      <Paragraph>
        <Text strong>c. Information from partners:</Text>
        <br />
        If you are a member of a gym, wellness, or beauty venue connected through ClubPass, we may
        receive booking and attendance data from those partners via the TYP platform integration.
      </Paragraph>

      <Title level={3}>3. How We Use Your Information</Title>
      <Paragraph>We use your personal data to:</Paragraph>
      <ul>
        <li>Provide and manage access to the ClubPass platform</li>
        <li>Enable you to explore, book, and attend classes, treatments, or services</li>
        <li>Process payments and manage subscriptions through PAYMIX</li>
        <li>Personalize your experience and suggest new venues or services</li>
        <li>Communicate updates, promotions, and service notifications</li>
        <li>Ensure the security and proper functioning of the Platform</li>
        <li>Comply with legal obligations and resolve disputes</li>
      </ul>
      <Paragraph>
        We will only use your data when we have a lawful basis to do so (such as contract
        performance, legitimate interest, consent, or legal compliance).
      </Paragraph>

      <Title level={3}>4. Sharing of Information</Title>
      <Paragraph>We only share your data with:</Paragraph>
      <ul>
        <li>
          Service providers that help us run the Platform (hosting, analytics, customer support,
          marketing tools)
        </li>
        <li>Payment processor PAYMIX for handling transactions securely</li>
        <li>
          Partner venues (gyms, studios, salons, wellness centers) for booking and attendance
          management
        </li>
        <li>Authorities or regulators, when legally required</li>
      </ul>
      <Paragraph>
        All partners and service providers are bound by strict confidentiality and data protection
        agreements.
      </Paragraph>

      <Title level={3}>5. Data Transfers</Title>
      <Paragraph>
        Your data may be transferred outside the European Economic Area (EEA) for hosting or service
        provision purposes. When this happens, we ensure adequate safeguards are in place, such as
        UK/EU-approved Standard Contractual Clauses or equivalent protections.
      </Paragraph>

      <Title level={3}>6. Data Retention</Title>
      <Paragraph>We keep your data only as long as necessary to:</Paragraph>
      <ul>
        <li>Provide you with the ClubPass service</li>
        <li>Comply with legal and financial requirements</li>
        <li>Resolve disputes and enforce agreements</li>
      </ul>
      <Paragraph>Inactive accounts may be deleted after 24 months of inactivity.</Paragraph>

      <Title level={3}>7. Your Rights</Title>
      <Paragraph>You have the following rights under the GDPR:</Paragraph>
      <ul>
        <li>Access your data</li>
        <li>Correct inaccurate information</li>
        <li>Delete your data (“right to be forgotten”)</li>
        <li>Restrict or object to processing</li>
        <li>Port your data to another service</li>
        <li>Withdraw consent (where applicable)</li>
      </ul>
      <Paragraph>
        To exercise your rights, contact us at{" "}
        <Link href="mailto:kg@clubpass.global">kg@clubpass.global</Link>.
      </Paragraph>

      <Title level={3}>8. Data Security</Title>
      <Paragraph>
        We implement appropriate technical and organizational measures to protect your personal
        data, including encryption, secure servers, access control, and regular audits.
      </Paragraph>

      <Title level={3}>9. Children’s Privacy</Title>
      <Paragraph>
        ClubPass is intended for users aged 16 and over. We do not knowingly collect data from
        minors. If you believe a child has provided us with personal data, please contact us
        immediately.
      </Paragraph>

      <Title level={3}>10. Updates to This Policy</Title>
      <Paragraph>
        We may update this Privacy Policy periodically to reflect changes in our practices or for
        legal reasons. Updates will be posted on this page with the revised date.
      </Paragraph>
    </div>
  );
};
